import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { LeadSourceNode, SalesConfig } from '@gooduncles/gu-app-schema';
import { Button, Divider, Drawer, Empty, Input, Popconfirm, Space, Switch, Tag, Typography, message } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';

import { getSalesConfig, setSalesConfig, updateSalesConfig } from 'src/utils/firebase/firebase-short-cut';

const MAX_DEPTH = 3;
const DEPTH_LABELS = ['유입 경로', '유입 상세', '유입 담당자'];
const DEPTH_COLORS = ['blue', 'green', 'orange'];

interface SalesConfigDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** 빈 SalesConfig 기본값 */
const emptySalesConfig: SalesConfig = {
  leadSourceTree: [],
  setupStatuses: [],
};

// ─── LeadSourceTree Editor ──────────────────────────────────

interface TreeNodeEditorProps {
  nodes: LeadSourceNode[];
  depth: number;
  onUpdate: (nodes: LeadSourceNode[]) => void;
}

const TreeNodeEditor: FC<TreeNodeEditorProps> = ({ nodes, depth, onUpdate }) => {
  const [addingLabel, setAddingLabel] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingLabel, setEditingLabel] = useState('');

  const hasDuplicate = (label: string, excludeIndex?: number) =>
    nodes.some((n, i) => i !== excludeIndex && n.label === label.trim());

  const handleAdd = () => {
    const trimmed = addingLabel.trim();
    if (!trimmed) return;
    if (hasDuplicate(trimmed)) {
      message.error('동일한 이름이 이미 존재합니다.');
      return;
    }
    onUpdate([...nodes, { label: trimmed, children: [] }]);
    setAddingLabel('');
    setIsAdding(false);
  };

  const handleEdit = (index: number) => {
    const trimmed = editingLabel.trim();
    if (!trimmed) return;
    if (hasDuplicate(trimmed, index)) {
      message.error('동일한 이름이 이미 존재합니다.');
      return;
    }
    const updated = nodes.map((n, i) => (i === index ? { ...n, label: trimmed } : n));
    onUpdate(updated);
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    onUpdate(nodes.filter((_, i) => i !== index));
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= nodes.length) return;
    const updated = [...nodes];
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
    onUpdate(updated);
  };

  const handleToggleChildren = (index: number, checked: boolean) => {
    const node = nodes[index];
    if (!checked && node.children && node.children.length > 0) {
      // 고정 선택지 → 자유 입력: 하위 노드 삭제 경고는 Popconfirm으로 처리
      return;
    }
    const updated = nodes.map((n, i) => (i === index ? { ...n, children: checked ? [] : null } : n));
    onUpdate(updated);
  };

  const handleConfirmToFreeInput = (index: number) => {
    const updated = nodes.map((n, i) => (i === index ? { ...n, children: null } : n));
    onUpdate(updated);
  };

  const handleChildrenUpdate = (index: number, children: LeadSourceNode[]) => {
    const updated = nodes.map((n, i) => (i === index ? { ...n, children } : n));
    onUpdate(updated);
  };

  return (
    <div style={{ paddingLeft: depth > 0 ? 24 : 0 }}>
      {nodes.map((node, index) => (
        <div key={`${depth}-${index}`} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <Tag color={DEPTH_COLORS[depth]}>{DEPTH_LABELS[depth]}</Tag>

            {editingIndex === index ? (
              <Space.Compact>
                <Input
                  size='small'
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onPressEnter={() => handleEdit(index)}
                  autoFocus
                  style={{ width: 160 }}
                />
                <Button size='small' type='primary' onClick={() => handleEdit(index)}>
                  확인
                </Button>
                <Button size='small' onClick={() => setEditingIndex(null)}>
                  취소
                </Button>
              </Space.Compact>
            ) : (
              <Typography.Text strong>{node.label}</Typography.Text>
            )}

            {node.children === null && <Tag>자유 입력</Tag>}

            {editingIndex !== index && (
              <Space size={4} style={{ marginLeft: 'auto' }}>
                {depth < MAX_DEPTH - 1 && (
                  <>
                    {node.children !== null ? (
                      <Popconfirm
                        title='자유 입력으로 전환하시겠습니까?'
                        description='기존 하위 옵션이 모두 삭제됩니다.'
                        onConfirm={() => handleConfirmToFreeInput(index)}
                        disabled={!node.children || node.children.length === 0}
                      >
                        <Switch
                          size='small'
                          checked={node.children !== null}
                          onChange={(checked) => {
                            if (!checked && node.children && node.children.length > 0) {
                              // Popconfirm이 처리
                              return;
                            }
                            handleToggleChildren(index, checked);
                          }}
                          checkedChildren='고정'
                          unCheckedChildren='자유'
                        />
                      </Popconfirm>
                    ) : (
                      <Switch
                        size='small'
                        checked={false}
                        onChange={(checked) => handleToggleChildren(index, checked)}
                        checkedChildren='고정'
                        unCheckedChildren='자유'
                      />
                    )}
                  </>
                )}
                <Button
                  size='small'
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => handleMove(index, 'up')}
                />
                <Button
                  size='small'
                  icon={<ArrowDownOutlined />}
                  disabled={index === nodes.length - 1}
                  onClick={() => handleMove(index, 'down')}
                />
                <Button
                  size='small'
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingIndex(index);
                    setEditingLabel(node.label);
                  }}
                />
                <Popconfirm
                  title='이 옵션을 삭제하시겠습니까?'
                  description={
                    node.children && node.children.length > 0
                      ? '하위 옵션도 함께 삭제됩니다. 기존 매장 데이터에는 영향이 없지만, 앞으로 이 옵션을 선택할 수 없습니다.'
                      : '기존 매장 데이터에는 영향이 없지만, 앞으로 이 옵션을 선택할 수 없습니다.'
                  }
                  onConfirm={() => handleDelete(index)}
                >
                  <Button size='small' danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            )}
          </div>

          {/* 하위 노드 재귀 렌더링 */}
          {node.children !== null && depth < MAX_DEPTH - 1 && (
            <TreeNodeEditor
              nodes={node.children}
              depth={depth + 1}
              onUpdate={(children) => handleChildrenUpdate(index, children)}
            />
          )}
        </div>
      ))}

      {/* 추가 UI */}
      {isAdding ? (
        <div style={{ paddingLeft: depth > 0 ? 0 : 0, marginTop: 4 }}>
          <Space.Compact>
            <Input
              size='small'
              placeholder={`${DEPTH_LABELS[depth]} 이름`}
              value={addingLabel}
              onChange={(e) => setAddingLabel(e.target.value)}
              onPressEnter={handleAdd}
              autoFocus
              style={{ width: 160 }}
            />
            <Button size='small' type='primary' onClick={handleAdd}>
              추가
            </Button>
            <Button
              size='small'
              onClick={() => {
                setIsAdding(false);
                setAddingLabel('');
              }}
            >
              취소
            </Button>
          </Space.Compact>
        </div>
      ) : (
        <Button
          size='small'
          type='dashed'
          icon={<PlusOutlined />}
          onClick={() => setIsAdding(true)}
          style={{ marginTop: 4 }}
        >
          {DEPTH_LABELS[depth]} 추가
        </Button>
      )}
    </div>
  );
};

// ─── SetupStatuses Editor ──────────────────────────────────

interface SetupStatusesEditorProps {
  statuses: string[];
  onUpdate: (statuses: string[]) => void;
}

const SetupStatusesEditor: FC<SetupStatusesEditorProps> = ({ statuses, onUpdate }) => {
  const [addingValue, setAddingValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const hasDuplicate = (value: string, excludeIndex?: number) =>
    statuses.some((s, i) => i !== excludeIndex && s === value.trim());

  const handleAdd = () => {
    const trimmed = addingValue.trim();
    if (!trimmed) return;
    if (hasDuplicate(trimmed)) {
      message.error('동일한 상태명이 이미 존재합니다.');
      return;
    }
    onUpdate([...statuses, trimmed]);
    setAddingValue('');
    setIsAdding(false);
  };

  const handleEdit = (index: number) => {
    const trimmed = editingValue.trim();
    if (!trimmed) return;
    if (hasDuplicate(trimmed, index)) {
      message.error('동일한 상태명이 이미 존재합니다.');
      return;
    }
    onUpdate(statuses.map((s, i) => (i === index ? trimmed : s)));
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    onUpdate(statuses.filter((_, i) => i !== index));
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= statuses.length) return;
    const updated = [...statuses];
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
    onUpdate(updated);
  };

  return (
    <div>
      {statuses.length === 0 && !isAdding && <Empty description='등록된 진행 상태가 없습니다.' />}
      {statuses.map((status, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          {editingIndex === index ? (
            <Space.Compact>
              <Input
                size='small'
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onPressEnter={() => handleEdit(index)}
                autoFocus
                style={{ width: 160 }}
              />
              <Button size='small' type='primary' onClick={() => handleEdit(index)}>
                확인
              </Button>
              <Button size='small' onClick={() => setEditingIndex(null)}>
                취소
              </Button>
            </Space.Compact>
          ) : (
            <Typography.Text>{status}</Typography.Text>
          )}

          {editingIndex !== index && (
            <Space size={4} style={{ marginLeft: 'auto' }}>
              <Button
                size='small'
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => handleMove(index, 'up')}
              />
              <Button
                size='small'
                icon={<ArrowDownOutlined />}
                disabled={index === statuses.length - 1}
                onClick={() => handleMove(index, 'down')}
              />
              <Button
                size='small'
                icon={<EditOutlined />}
                onClick={() => {
                  setEditingIndex(index);
                  setEditingValue(status);
                }}
              />
              <Popconfirm
                title='이 상태를 삭제하시겠습니까?'
                description='기존 매장 데이터에는 영향이 없지만, 앞으로 이 상태를 선택할 수 없습니다.'
                onConfirm={() => handleDelete(index)}
              >
                <Button size='small' danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          )}
        </div>
      ))}

      {isAdding ? (
        <div style={{ marginTop: 8 }}>
          <Space.Compact>
            <Input
              size='small'
              placeholder='진행 상태명'
              value={addingValue}
              onChange={(e) => setAddingValue(e.target.value)}
              onPressEnter={handleAdd}
              autoFocus
              style={{ width: 160 }}
            />
            <Button size='small' type='primary' onClick={handleAdd}>
              추가
            </Button>
            <Button
              size='small'
              onClick={() => {
                setIsAdding(false);
                setAddingValue('');
              }}
            >
              취소
            </Button>
          </Space.Compact>
        </div>
      ) : (
        <Button
          size='small'
          type='dashed'
          icon={<PlusOutlined />}
          onClick={() => setIsAdding(true)}
          style={{ marginTop: 8 }}
        >
          진행 상태 추가
        </Button>
      )}
    </div>
  );
};

// ─── Main Drawer ──────────────────────────────────

const SalesConfigDrawer: FC<SalesConfigDrawerProps> = ({ open, onClose }) => {
  const [config, setConfig] = useState<SalesConfig>(emptySalesConfig);
  const [loading, setLoading] = useState(false);

  // 문서 로드 (없으면 초기 생성)
  useEffect(() => {
    if (!open) return;

    setLoading(true);
    getSalesConfig()
      .then((doc) => {
        if (doc) {
          setConfig({
            leadSourceTree: doc.leadSourceTree ?? [],
            setupStatuses: doc.setupStatuses ?? [],
          });
        } else {
          // 문서가 없으면 빈 상태로 초기 생성
          setSalesConfig(emptySalesConfig);
          setConfig(emptySalesConfig);
        }
      })
      .catch((error) => {
        console.error('Failed to load SalesConfig:', error);
        message.error('설정을 불러오는데 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleUpdateTree = useCallback(async (leadSourceTree: LeadSourceNode[]) => {
    setConfig((prev) => ({ ...prev, leadSourceTree }));
    try {
      await updateSalesConfig({ leadSourceTree });
    } catch (error) {
      console.error('Failed to update leadSourceTree:', error);
      message.error('유입경로 트리 저장에 실패했습니다.');
    }
  }, []);

  const handleUpdateStatuses = useCallback(async (setupStatuses: string[]) => {
    setConfig((prev) => ({ ...prev, setupStatuses }));
    try {
      await updateSalesConfig({ setupStatuses });
    } catch (error) {
      console.error('Failed to update setupStatuses:', error);
      message.error('진행 상태 저장에 실패했습니다.');
    }
  }, []);

  return (
    <Drawer title='영업 설정' open={open} onClose={onClose} width={640} loading={loading}>
      <Typography.Title level={5}>유입경로 트리</Typography.Title>
      <Typography.Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
        유입 경로 → 유입 상세 → 유입 담당자 순서로 계층 옵션을 관리합니다.
      </Typography.Text>
      {config.leadSourceTree.length === 0 ? (
        <div style={{ marginBottom: 8 }}>
          <Empty description='등록된 유입경로가 없습니다.' />
        </div>
      ) : null}
      <TreeNodeEditor nodes={config.leadSourceTree} depth={0} onUpdate={handleUpdateTree} />

      <Divider />

      <Typography.Title level={5}>진행 상태 목록</Typography.Title>
      <Typography.Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
        매장의 영업 진행 상태 선택지를 관리합니다.
      </Typography.Text>
      <SetupStatusesEditor statuses={config.setupStatuses} onUpdate={handleUpdateStatuses} />
    </Drawer>
  );
};

export default SalesConfigDrawer;
